import * as Yup from 'yup';

import { FIELD_TYPES, LANGUAGES } from './constants';
import { getTranslation } from './translations';

/**
 * The address of the page the widget is embedded on, from its embed code or the referrer. Empty when the
 * widget is opened on its own, so its own address is never taken for the customer's page.
 */
export const getEmbedPageUrl = () => {
  if (typeof window === 'undefined') return '';
  const locationParam = new URLSearchParams(window.location.search).get('location');
  return locationParam || (typeof document !== 'undefined' && document.referrer) || '';
};

export const getEmbedWebsiteUrl = () =>
  getEmbedPageUrl() || (typeof window === 'undefined' ? '' : window.location.href);

export const toStrCamelCase = str => {
  return str
    .toLowerCase()
    .split(' ')
    .map((word, index) => (index === 0 ? word : word.charAt(0).toUpperCase() + word.slice(1)))
    .join('');
};

export const toCamelCase = obj => {
  return Object.fromEntries(
    Object.entries(obj).map(([key, value]) => {
      let camelKey = key
        .replace(/\s(.)/g, match => match.toUpperCase())
        .replace(/\s/g, '')
        .replace(/^(.)/, match => match.toLowerCase());
      return [camelKey, value];
    })
  );
};

export const toSnakeCase = obj => {
  return Object.fromEntries(
    Object.entries(obj).map(([key, value]) => {
      let snakeKey = key.toLowerCase().replace(/\s+/g, '_');
      return [snakeKey, value];
    })
  );
};

export const capitalizeText = text => {
  if (!text) return '';
  return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
};

export const getFileNameFromUrl = url => {
  if (typeof url !== 'string') return '';

  const fileName = url.split('/').pop()?.split('?')[0] || '';
  const dotIndex = fileName.lastIndexOf('.');
  const underscoreIndex = fileName.lastIndexOf('_');

  if (dotIndex === -1 || underscoreIndex === -1) return fileName;

  const name = fileName.slice(0, underscoreIndex);
  const extension = fileName.slice(dotIndex);

  return `${name}${extension}`;
};

export const replaceWhiteSpaceWithUnderscore = str => {
  return str.replace(/\s+/g, '_');
};

export const getSelectPickerValue = (label, isMultiSelect, fieldItems, values) => {
  if (isMultiSelect) {
    return values?.[label] || [];
  }

  return fieldItems.find(item => primaryLabelText(item.label) === values?.[label]);
};

export const getFieldPlaceholder = (fieldType, label, isProcessingFiles, lang = LANGUAGES[0].code) => {
  const isFile = fieldType === FIELD_TYPES.UPLOAD;

  if (isFile && isProcessingFiles) return getTranslation('uploading', lang);

  return capitalizeText(label);
};

export const generateValidationSchema = (fields, lang = LANGUAGES[0].code) => {
  const schema = {};
  const requiredMsg = getTranslation('validation.required', lang);
  const invalidEmailMsg = getTranslation('validation.invalidEmail', lang);
  const numericOnlyMsg = getTranslation('validation.numericOnly', lang);
  fields?.forEach(({ label, type, isRequired, isMultiSelect }) => {
    const fieldKey = primaryLabelText(label);
    if (!fieldKey) return;
    const fieldType = type?.toLowerCase();
    let validator;

    if (fieldType === FIELD_TYPES.DROPDOWN && isMultiSelect) {
      validator = Yup.array()
        .of(Yup.string())
        .test('nonEmptyArray', requiredMsg, value => !isRequired || (Array.isArray(value) && value.length > 0));
    } else {
      switch (fieldType) {
      case FIELD_TYPES.EMAIL:
        validator = Yup.string().email(invalidEmailMsg);
        break;
      case FIELD_TYPES.NUMBER:
        validator = Yup.string().matches(/^\d+$/, numericOnlyMsg);
        break;
      case FIELD_TYPES.UPLOAD:
        validator = Yup.mixed().test(
          'fileRequired',
          requiredMsg,
          value => !isRequired || (value && value.length > 0)
        );
        break;
      case FIELD_TYPES.DATE:
        validator = Yup.mixed().test('validDate', requiredMsg, value => {
          const isDate = val => val instanceof Date && !isNaN(val.getTime());

          if (!value) return !isRequired;

          return isDate(value);
        });
        break;
      case FIELD_TYPES.CONSENT:
        validator = Yup.boolean();
        break;
      default:
        validator = Yup.string();
      }

      if (isRequired && fieldType !== FIELD_TYPES.UPLOAD && fieldType !== FIELD_TYPES.CONSENT) {
        validator = validator.required(requiredMsg);
      }
    }

    schema[fieldKey] = validator;
  });
  return Yup.object().shape(schema);
};

export const truncateText = (text, maxLength = 30) => {
  if (!text || text.length <= maxLength) return text;
  return text.slice(0, maxLength) + '...';
};

/**
 * Extracts a language code from a label like "Swedish (SV)".
 *
 * @param {string} label - The language label from the API.
 * @returns {string|null} The normalized language code or null if not found.
 */
const getLanguageCodeFromLabel = label => {
  if (typeof label !== 'string') return null;

  const codeMatch = label.match(/\(([^)]+)\)/);
  if (codeMatch?.[1]) return codeMatch[1].trim().toLowerCase();

  const normalized = label.toLowerCase();
  if (normalized.includes('english')) return 'en';
  if (normalized.includes('finnish') || normalized.includes('suomi')) return 'fi';
  if (normalized.includes('swedish') || normalized.includes('svenska')) return 'sv';
  if (normalized.includes('german') || normalized.includes('deutsch')) return 'de';

  return null;
};

/**
 * Checks if a target language code is enabled in the languagesEnabled array.
 *
 * @param {Array} languagesEnabled - The languagesEnabled array from the API.
 * @param {string} targetCode - The language code to verify (e.g., 'en').
 * @returns {boolean} True if the target language is enabled, otherwise false.
 */
export const isLanguageEnabled = (languagesEnabled, targetCode) => {
  if (!Array.isArray(languagesEnabled) || !targetCode) return false;

  return languagesEnabled.some(({ label, value }) => {
    const code = getLanguageCodeFromLabel(label);
    return code === targetCode && value === true;
  });
};

/**
 * Returns the list of enabled language codes based on the languagesEnabled array.
 * Falls back to the default LANGUAGES order.
 *
 * @param {Array} languagesEnabled - Array of language objects with label/value.
 * @returns {string[]} Array of enabled language codes (e.g., ['en', 'fi'])
 */
export const getEnabledLanguageCodes = languagesEnabled => {
  if (!Array.isArray(languagesEnabled) || languagesEnabled.length === 0) {
    return LANGUAGES.map(lang => lang.code);
  }

  const supportedCodes = new Set(LANGUAGES.map(({ code }) => code));

  const enabledCodes = languagesEnabled
    .map((lang, index) => ({
      code: getLanguageCodeFromLabel(lang.label),
      value: lang.value,
      order: typeof lang.order === 'number' ? lang.order : Number.MAX_SAFE_INTEGER,
      index,
    }))
    .filter(({ code, value }) => supportedCodes.has(code) && value === true)
    .sort((a, b) => {
      if (a.order === b.order) return a.index - b.index;
      return a.order - b.order;
    })
    .map(({ code }) => code);

  const uniqueEnabledCodes = [...new Set(enabledCodes)];

  return uniqueEnabledCodes.length ? uniqueEnabledCodes : LANGUAGES.map(lang => lang.code);
};

/**
 * Returns the localized value for a given custom and default value based on the provided active language.
 *
 * It attempts to find the value in the following order:
 *   1. customValue for the activeLang
 *   2. customValue in English ('en')
 *   3. defaultValue for the activeLang
 *   4. defaultValue in English ('en')
 *
 * @param {Object} customValue - The custom object containing key/value pairs of language codes and values.
 * @param {Object} defaultValue - The default object containing key/value pairs of language codes and values.
 * @param {string} activeLang - The active language code to use for localization.
 * @returns {string|undefined} The localized value based on the priority order, or undefined if not found.
 */

export const getLocalizedValue = (customValue, defaultValue, activeLang) =>
  customValue?.[activeLang] || customValue?.en || defaultValue?.[activeLang] || defaultValue?.en;

/**
 * Language order used to resolve a per-language label dict to a single stable string.
 * Matches the backend `settings.LANGUAGES` order so the submitted response key never
 * depends on the visitor's active language.
 */
export const PRIMARY_LABEL_LANGUAGE_ORDER = ['fi', 'en', 'de', 'sv'];

/**
 * Resolves a lead-field label to a single plain string.
 * Mirrors the backend `primary_label_text`: returns the first non-empty value in
 * PRIMARY_LABEL_LANGUAGE_ORDER. Accepts a legacy plain string for backward safety.
 *
 * @param {Object|string} label - Per-language label dict or a legacy plain string.
 * @returns {string} The primary-language label text, or an empty string.
 */
export const primaryLabelText = label => {
  if (label && typeof label === 'object') {
    const code = PRIMARY_LABEL_LANGUAGE_ORDER.find(langCode => label[langCode]);
    return code ? label[code] : '';
  }
  return typeof label === 'string' ? label : '';
};

export const defaultEmptyLangObject = Object.fromEntries(LANGUAGES.map(({ code }) => [code, '']));

export const normalizeInpageLabel = (label, empty = defaultEmptyLangObject) => {
  if (label == null) return { ...empty };
  if (typeof label === 'string') {
    const trimmed = label.trim();
    if (trimmed.startsWith('{') && trimmed.endsWith('}')) {
      try {
        const parsed = JSON.parse(trimmed);
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
          return { ...empty, ...parsed };
        }
      } catch {
        /* fall through to legacy single-language string */
      }
    }
    return { ...empty, en: trimmed };
  }
  if (typeof label === 'object' && !Array.isArray(label)) return { ...empty, ...label };
  return { ...empty };
};

export const formatCurrentTime = () => {
  const now = new Date();
  const day = String(now.getDate()).padStart(2, '0');
  const month = now.toLocaleString('en-GB', { month: 'short' }).toLowerCase();
  const year = now.getFullYear();
  const hours = now.getHours();
  const period = hours >= 12 ? 'pm' : 'am';
  const formattedHour = ((hours + 11) % 12) + 1;
  const minutes = String(now.getMinutes()).padStart(2, '0');

  return `${day}-${month}-${year} : ${formattedHour}:${minutes}${period}`;
};

/**
 * Formats the current time in the format of day-month-year:hour:minute:period.
 *
 * @returns {string} The formatted current time.
 */

export const formatTimeStamp = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  const hours = String(now.getHours()).padStart(2, '0');
  const minutes = String(now.getMinutes()).padStart(2, '0');

  return `${year}-${month}-${day} ${hours}:${minutes}`;
};

/**
 * Creates a payload for tracking a click event.
 *
 * @param {string} elementType - The type of element that was clicked (e.g., 'CTA', 'BUTTON', 'LINK').
 * @param {string} elementId - The ID of the element that was clicked.
 * @returns {Object} The payload object containing the element type and ID.
 */

export const createTrackClickPayload = (elementType, elementId) => ({
  payload: {
    elementType,
    elementId,
  },
});

export const mapTextStyles = settings => ({
  bold: settings?.isBold ?? false,
  italic: settings?.isItalic ?? false,
  underline: settings?.isUnderlined ?? false,
});

const FONT_WEIGHT_BOLD = 700;

/**
 * Inline styles for theme text blocks (heading / subheading).
 * @param {number} [size] - Font size in px
 * @param {{ bold?: boolean, italic?: boolean, underline?: boolean }} styles
 * @param {string} [alignment]
 * @param {string} [color]
 */
export const getTextStyle = (size, styles = {}, alignment = 'center', color) => {
  const normalized =
    styles && (styles.bold !== undefined || styles.italic !== undefined || styles.underline !== undefined)
      ? styles
      : mapTextStyles(styles);
  const align =
    typeof alignment === 'string' && alignment.length > 0 ? alignment.toLowerCase() : 'center';

  return {
    fontSize: size != null ? `${size}px` : undefined,
    fontWeight: normalized?.bold ? FONT_WEIGHT_BOLD : undefined,
    fontStyle: normalized?.italic ? 'italic' : undefined,
    textDecoration: normalized?.underline ? 'underline' : undefined,
    textAlign: align,
    color,
  };
};

export const findFaqById = (faqs, faqId) => faqs?.find(item => item?.id === faqId);

/**
 * Whether a quick question with a lead form should open its form straight away instead of asking the chatbot:
 * when it has no answer of its own, or when the owner wants its answer shown only once the form is sent.
 *
 * @param {Object} faq - The quick question, with its lead form and answer.
 * @returns {boolean} True when clicking it opens the form directly.
 */
export const opensLeadFormDirectly = faq =>
  Boolean(faq?.lead?.fields?.length) && (!faq?.answer?.trim() || Boolean(faq?.showAnswerAfterForm));

/**
 * Whether a quick question's answer waits for its form, and is asked once the form is sent.
 *
 * @param {Object} faq - The quick question, with its lead form and answer.
 * @returns {boolean} True when sending the form should ask the question.
 */
export const answersAfterForm = faq => opensLeadFormDirectly(faq) && Boolean(faq?.answer?.trim());

/**
 * The page address without its query and fragment, which can carry tokens or e-mail addresses and are not
 * needed to tell which page the visitor is on.
 *
 * @param {string} url - The full page address.
 * @returns {string|null} Origin and path, or null for an address that cannot be read.
 */
export const toPageAddress = url => {
  try {
    const { origin, pathname } = new URL(url);
    return `${origin}${pathname}`;
  } catch {
    return null;
  }
};
